import React, { useState, useEffect } from "react";
import { 
    FaStar, FaThumbsUp, FaThumbsDown, FaCommentAlt, 
    FaUser, FaCar, FaSearch, FaChevronLeft, FaChevronRight 
} from "react-icons/fa";
import api from "../../api/Api"; 

export default function AdminFeedbacks() {
    const [feedbacks, setFeedbacks] = useState([]);
    const [stats, setStats] = useState({ total: 0, averageRating: 0, recommended: 0 });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    
    // --- PAGINATION STATE ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    useEffect(() => {
        fetchFeedbacks();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const fetchFeedbacks = async () => {
        try {
            const token = localStorage.getItem("adminToken"); 
            
            const response = await api.get("/feedback/list", {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setFeedbacks(response.data.data);
                setStats(response.data.stats);
            }
        } catch (error) {
            console.error("Failed to load feedbacks", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredFeedbacks = feedbacks.filter(f => 
        (f.driver_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (f.customer_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (f.trip_id || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentData = filteredFeedbacks.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredFeedbacks.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const renderTags = (tagsData) => {
        try {
            const parsedTags = typeof tagsData === 'string' ? JSON.parse(tagsData) : tagsData;
            if (!Array.isArray(parsedTags) || parsedTags.length === 0) {
                return <span className="text-muted fst-italic mt-1 d-block" style={{ fontSize: "0.7rem" }}>No tags provided</span>;
            }

            const TAG_COLORS = [
                { bg: "#EFF6FF", text: "#2563EB", border: "#BFDBFE" }, 
                { bg: "#F0FDF4", text: "#16A34A", border: "#BBF7D0" }, 
                { bg: "#FFF7ED", text: "#EA580C", border: "#FED7AA" }, 
                { bg: "#FAF5FF", text: "#7C3AED", border: "#E9D5FF" }, 
                { bg: "#FDF2F8", text: "#C026D3", border: "#FBCFE8" }, 
                { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA" }, 
            ];

            return (
                <div className="d-flex flex-wrap gap-1 mt-2" style={{ maxWidth: "220px" }}>
                    {parsedTags.map((tag, i) => {
                        // 2. Cycle through the colors based on the index (0, 1, 2, 3...)
                        const theme = TAG_COLORS[i % TAG_COLORS.length]; 

                        return (
                            <span 
                                key={i} 
                                className="badge fw-semibold rounded-pill" 
                                style={{ 
                                    fontSize: "0.60rem", 
                                    padding: "4px 8px", 
                                    backgroundColor: theme.bg,       
                                    color: theme.text,               
                                    border: `1px solid ${theme.border}`, 
                                    letterSpacing: "0.2px"
                                }}
                            >
                                {tag}
                            </span>
                        );
                    })}
                </div>
            );
        } catch (e) {
            return null;
        }
    };

    return (
        <div className="p-2 w-100" style={{ backgroundColor: "#F4F7FE", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
            
            <style>{`
                .native-search-bar { background: #FFFFFF; border-radius: 100px; padding: 4px 16px; box-shadow: 0 2px 10px rgba(0,0,0,0.02); border: 1px solid #E2E8F0; transition: all 0.2s; }
                .native-search-bar:focus-within { box-shadow: 0 4px 14px rgba(0,0,0,0.05); border-color: #CBD5E1; }
                .native-search-input { border: none; outline: none; background: transparent; font-size: 0.85rem; width: 220px; padding: 6px 8px; }
                .stat-card-compact { border-radius: 20px; border: none; box-shadow: 0 4px 15px rgba(0,0,0,0.03); transition: transform 0.2s; }
                .stat-card-compact:hover { transform: translateY(-2px); }
                
                .compact-table th { 
                    font-size: 0.75rem; letter-spacing: 0.5px; text-transform: uppercase; color: #64748B; 
                    padding: 12px 16px; 
                    background-color: #ffffff; 
                    position: sticky; 
                    top: 0; 
                    z-index: 10;
                    box-shadow: 0 1px 0 #F1F5F9; 
                }
                .compact-table td { padding: 12px 16px; vertical-align: top; border-bottom: 1px solid #F1F5F9; }
                
                .table-wrapper::-webkit-scrollbar { width: 6px; }
                .table-wrapper::-webkit-scrollbar-track { background: transparent; }
                .table-wrapper::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 10px; }

                .page-btn { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 8px; border: 1px solid #E2E8F0; background: white; color: #475569; font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: all 0.2s; }
                .page-btn:hover:not(:disabled) { background: #F8FAFC; }
                .page-btn.active { background: #1E293B; color: white; border-color: #1E293B; }
                .page-btn:disabled { opacity: 0.5; cursor: not-allowed; }

                .tag-bubble {
                    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
                    color: #334155;
                    font-size: 0.72rem;
                    font-weight: 600;
                    padding: 5px 12px;
                    border-radius: 20px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.04), 0 1px 2px rgba(255,255,255,0.8) inset;
                    border: 1px solid #cbd5e1;
                    display: inline-flex;
                    align-items: center;
                    letter-spacing: 0.2px;
                }
                .tag-bubble-extra {
                    background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
                    color: #1d4ed8;
                    font-size: 0.72rem;
                    font-weight: 700;
                    padding: 5px 10px;
                    border-radius: 20px;
                    box-shadow: 0 2px 4px rgba(37,99,235,0.1), 0 1px 2px rgba(255,255,255,0.8) inset;
                    border: 1px solid #bfdbfe;
                    display: inline-flex;
                    align-items: center;
                }
            `}</style>

            {/* Header Area */}
            <div className="d-flex justify-content-between align-items-end mb-4">
                <div>
                    <h4 className="fw-bold mb-1" style={{ color: "#0F172A", letterSpacing: "-0.5px" }}>Customer Feedbacks</h4>
                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>Monitor driver ratings and trip reviews seamlessly.</p>
                </div>
                
                {/* Native Pill Search Bar */}
                <div className="native-search-bar d-flex align-items-center">
                    <FaSearch color="#94A3B8" size={14} />
                    <input 
                        type="text" 
                        className="native-search-input" 
                        placeholder="Search Customer, Trip ID..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Compact Stat Cards */}
            <div className="row mb-4 g-3">
                <div className="col-md-4">
                    <div className="card stat-card-compact h-100" style={{ background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)", color: "white" }}>
                        <div className="card-body p-3 px-4 d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="text-white-50 fw-bold mb-1" style={{ fontSize: "0.7rem", letterSpacing: "1px" }}>TOTAL REVIEWS</h6>
                                <h3 className="fw-bold mb-0">{stats.total}</h3>
                            </div>
                            <div className="bg-white bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                                <FaCommentAlt size={20} color="#F8FAFC" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card stat-card-compact h-100" style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", color: "white" }}>
                        <div className="card-body p-3 px-4 d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="text-white-50 fw-bold mb-1" style={{ fontSize: "0.7rem", letterSpacing: "1px" }}>AVG RATING</h6>
                                <h3 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                    {stats.averageRating} <FaStar size={18} color="#FEF3C7" />
                                </h3>
                            </div>
                            <div className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                                <FaStar size={20} color="#FFFFFF" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card stat-card-compact h-100" style={{ background: "linear-gradient(135deg, #10B981 0%, #059669 100%)", color: "white" }}>
                        <div className="card-body p-3 px-4 d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="text-white-50 fw-bold mb-1" style={{ fontSize: "0.7rem", letterSpacing: "1px" }}>RECOMMENDED TRIPS</h6>
                                <h3 className="fw-bold mb-0">{stats.recommended}</h3>
                            </div>
                            <div className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                                <FaThumbsUp size={20} color="#FFFFFF" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Feedback Table */}
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                
                {/* Scrollable Container with Sticky Header */}
                <div className="table-responsive table-wrapper" style={{ maxHeight: "calc(100vh - 320px)", overflowY: "auto" }}>
                    <table className="table table-hover compact-table mb-0">
                        <thead className="bg-white">
                            <tr>
                                <th>Date & Trip ID</th>
                                <th>Customer</th>
                                <th>Driver</th>
                                <th>Rating & Tags</th>
                                <th>Comments</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" className="text-center py-5 text-muted fw-medium">Loading feedbacks...</td></tr>
                            ) : currentData.length === 0 ? (
                                <tr><td colSpan="5" className="text-center py-5 text-muted fw-medium">No feedbacks found.</td></tr>
                            ) : (
                                currentData.map((item) => (
                                    <tr key={item.feedback_id}>
                                        {/* Date & Trip */}
                                        <td style={{ width: "15%" }}>
                                            <div className="fw-bold text-dark mb-1" style={{ fontSize: "0.85rem" }}>{item.trip_id}</div>
                                            <div className="text-muted" style={{ fontSize: "0.75rem", fontWeight: "500" }}>
                                                {new Date(item.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                            </div>
                                        </td>
                                        
                                        {/* Customer */}
                                        <td style={{ width: "20%" }}>
                                            <div className="d-flex align-items-center gap-2 mt-1">
                                                <div className="rounded-circle d-flex justify-content-center align-items-center flex-shrink-0" style={{ width: "32px", height: "32px", background: "#F1F5F9", color: "#64748B" }}>
                                                    <FaUser size={12} />
                                                </div>
                                                <div className="text-truncate">
                                                    <div className="fw-bold text-dark text-truncate" style={{ fontSize: "0.85rem" }}>{item.customer_name || "Guest"}</div>
                                                    <div className="text-muted" style={{ fontSize: "0.7rem", fontWeight: "500" }}>{item.customer_phone || "N/A"}</div>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Driver */}
                                        <td style={{ width: "20%" }}>
                                            <div className="d-flex align-items-center gap-2 mt-1">
                                                <div className="rounded-circle d-flex justify-content-center align-items-center flex-shrink-0" style={{ width: "32px", height: "32px", background: "#EFF6FF", color: "#3B82F6" }}>
                                                    <FaCar size={14} />
                                                </div>
                                                <div className="text-truncate">
                                                    <div className="fw-bold text-dark text-truncate" style={{ fontSize: "0.85rem" }}>{item.driver_name}</div>
                                                    <div className="text-muted" style={{ fontSize: "0.7rem", fontWeight: "500" }}>ID: {item.driver_id || "N/A"}</div>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Rating & Tags */}
                                        <td style={{ width: "25%" }}>
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <div className="d-flex text-warning">
                                                    {[...Array(5)].map((_, i) => (
                                                        <FaStar key={i} size={14} color={i < item.rating_star ? "#F59E0B" : "#E2E8F0"} />
                                                    ))}
                                                </div>
                                                <span className={`badge rounded-pill d-flex align-items-center justify-content-center ${item.recommend_driver ? 'bg-success' : 'bg-danger'} bg-opacity-10 text-${item.recommend_driver ? 'success' : 'danger'}`} style={{ width: "22px", height: "22px", padding: 0 }}>
                                                    {item.recommend_driver ? <FaThumbsUp size={10} /> : <FaThumbsDown size={10} />}
                                                </span>
                                            </div>
                                            {renderTags(item.tags)}
                                        </td>

                                        {/* Comments */}
                                        <td style={{ width: "20%" }}>
                                            <p className="mb-0 text-muted mt-1" style={{ fontSize: "0.82rem", lineHeight: "1.4", display: "-webkit-box", WebkitLineClamp: "3", WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {item.comment ? `"${item.comment}"` : <span className="fst-italic opacity-50">No remarks</span>}
                                            </p>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* --- PAGINATION CONTROLS (Fixed at bottom of table) --- */}
                {!loading && filteredFeedbacks.length > itemsPerPage && (
                    <div className="d-flex justify-content-between align-items-center p-3 border-top bg-white" style={{ borderColor: "#F1F5F9" }}>
                        <span className="text-muted fw-medium" style={{ fontSize: "0.80rem" }}>
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredFeedbacks.length)} of {filteredFeedbacks.length} entries
                        </span>
                        
                        <div className="d-flex gap-1">
                            <button 
                                className="page-btn" 
                                onClick={() => handlePageChange(currentPage - 1)} 
                                disabled={currentPage === 1}
                            >
                                <FaChevronLeft size={10} />
                            </button>
                            
                            {/* Render Page Numbers */}
                            {[...Array(totalPages)].map((_, index) => {
                                const page = index + 1;
                                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                                    return (
                                        <button 
                                            key={page} 
                                            className={`page-btn ${currentPage === page ? 'active' : ''}`}
                                            onClick={() => handlePageChange(page)}
                                        >
                                            {page}
                                        </button>
                                    );
                                } else if (page === currentPage - 2 || page === currentPage + 2) {
                                    return <span key={page} className="text-muted px-1 align-self-end">...</span>;
                                }
                                return null;
                            })}

                            <button 
                                className="page-btn" 
                                onClick={() => handlePageChange(currentPage + 1)} 
                                disabled={currentPage === totalPages}
                            >
                                <FaChevronRight size={10} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}